import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Building2, Search, RefreshCw, Loader2, AlertTriangle, Power, Trash2, RotateCcw,
  ShieldAlert, Users, BarChart3, Calendar, Clock, AlertCircle, CheckCircle2, Bot, Play,
  UserCog, Plus, Pencil, Copy, KeyRound, UserPlus, ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useConfirmDialog } from '@/components/ConfirmDialog';

const STATUS_META = {
  active:           { label: 'Active',           cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30' },
  trial:            { label: 'Trial',            cls: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30' },
  suspended:        { label: 'Suspended',        cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30' },
  pending_deletion: { label: 'Pending deletion', cls: 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30' },
  pending_approval: { label: 'Pending approval', cls: 'bg-purple-500/15 text-purple-700 dark:text-purple-300 border-purple-500/30' },
};

const REASON_PRESETS = [
  'trial_expired_no_payment', 'non_payment', 'customer_request',
  'abuse_or_tos_violation', 'duplicate_account', 'manual',
];

function fmtTime(iso) { if (!iso) return '—'; try { return new Date(iso).toLocaleDateString(); } catch { return iso; } }

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status || 'unknown', cls: 'bg-muted text-muted-foreground' };
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-medium ${meta.cls}`}>{meta.label}</span>;
}

function CountCard({ label, value, tone = 'default', icon: Icon }) {
  const tones = {
    default: 'bg-card border',
    warn:    'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300',
    bad:     'bg-rose-500/10 border-rose-500/30 text-rose-700 dark:text-rose-300',
    info:    'bg-blue-500/10 border-blue-500/30 text-blue-700 dark:text-blue-300',
  };
  return (
    <div className={`rounded-xl border p-3 ${tones[tone]}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider opacity-80">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className="text-xl font-semibold mt-0.5">{value}</div>
    </div>
  );
}

export default function AdminTenantsPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [cleanup, setCleanup] = useState(null);
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetPreview, setResetPreview] = useState(null);
  const [resetPhrase, setResetPhrase] = useState('');
  const [resetBusy, setResetBusy] = useState(false);

  // Action dialog state
  const [actionTenant, setActionTenant] = useState(null);   // { tenant, kind: 'disable'|'soft'|'hard' }
  // Users-of-tenant drawer state: holds the currently inspected tenant so we
  // can lazy-load its user list and show inline admin actions.
  const [usersTenant, setUsersTenant] = useState(null);
  const [reason, setReason] = useState('manual');
  const [customReason, setCustomReason] = useState('');
  const [confirmName, setConfirmName] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', domain: '', plan: 'standard', max_users: 10, admin_email: '', admin_name: '' });
  const [createBusy, setCreateBusy] = useState(false);
  const [seededAdmin, setSeededAdmin] = useState(null);   // { email, temp_password, email_sent, reset_link }
  // Inline edit-tenant dialog (plan + max users)
  const [editTenant, setEditTenant] = useState(null);
  const [editForm, setEditForm] = useState({ plan: 'standard', max_users: 10 });
  const [editBusy, setEditBusy] = useState(false);
  const [resendingId, setResendingId] = useState(null);

  const resendInvite = async (t) => {
    setResendingId(t.tenant_id);
    try {
      const r = await api.resendTenantAdminInvite(t.tenant_id);
      setSeededAdmin({ email: r.email, email_sent: r.email_sent, reset_link: r.reset_link, resent: true });
    } catch (err) { toast.error(err.message || 'Could not resend invite'); }
    finally { setResendingId(null); }
  };

  const createTenant = async () => {
    if (!createForm.name.trim()) { toast.error('Tenant name is required'); return; }
    const adminEmail = createForm.admin_email.trim();
    if (adminEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adminEmail)) { toast.error('Enter a valid admin email'); return; }
    setCreateBusy(true);
    try {
      const t = await api.createTenant({
        name: createForm.name.trim(),
        domain: createForm.domain.trim(),
        plan: createForm.plan,
        max_users: Number(createForm.max_users) || 10,
        ...(adminEmail ? { admin_email: adminEmail, admin_name: createForm.admin_name.trim() } : {}),
      });
      toast.success(`Tenant "${t.name}" created`);
      setCreateOpen(false);
      setCreateForm({ name: '', domain: '', plan: 'standard', max_users: 10, admin_email: '', admin_name: '' });
      if (t.admin_error) toast.warning(t.admin_error);
      else if (t.admin) setSeededAdmin(t.admin);
      await load();
    } catch (err) { toast.error(err.message || 'Failed to create tenant'); }
    finally { setCreateBusy(false); }
  };

  const openEdit = (t) => { setEditTenant(t); setEditForm({ plan: t.plan || 'standard', max_users: t.max_users ?? 10 }); };  const saveEdit = async () => {
    if (!editTenant) return;
    setEditBusy(true);
    try {
      await api.updateTenant(editTenant.tenant_id, { plan: editForm.plan, max_users: Number(editForm.max_users) || 1 });
      toast.success(`Updated "${editTenant.name}"`);
      setEditTenant(null);
      await load();
    } catch (err) { toast.error(err.message || 'Failed to update tenant'); }
    finally { setEditBusy(false); }
  };

  const isSuperAdmin = user?.role === 'super_admin';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, cleanupRes] = await Promise.all([
        api.listAdminTenants({ q: q.trim() || undefined, status_filter: statusFilter === 'all' ? undefined : statusFilter }),
        api.getTenantCleanupStatus().catch(() => null),
      ]);
      setData(res);
      if (cleanupRes) setCleanup(cleanupRes);
      // drop any selected ids that no longer exist
      const ids = new Set((res.tenants || []).map(t => t.tenant_id));
      setSelected(prev => new Set([...prev].filter(id => ids.has(id))));
    } catch (err) {
      toast.error(err.message || 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter]);

  const triggerCleanup = async () => {
    setCleanupRunning(true);
    try {
      const summary = await api.runTenantCleanup();
      const susp = summary?.auto_suspended?.suspended?.length || 0;
      const purg = summary?.auto_purged?.purged?.length || 0;
      toast.success(`Cleanup ran — auto-suspended ${susp}, auto-purged ${purg}`);
      await load();
    } catch (err) {
      toast.error(err.message || 'Cleanup failed');
    } finally {
      setCleanupRunning(false);
    }
  };

  useEffect(() => { if (isSuperAdmin) load(); else setLoading(false); }, [load, isSuperAdmin]);

  const openReset = async () => {
    setResetOpen(true);
    setResetPhrase('');
    setResetPreview(null);
    try { setResetPreview(await api.productionCleanup(true)); }
    catch (err) { toast.error(err.message || 'Preview failed'); }
  };

  const runReset = async () => {
    setResetBusy(true);
    try {
      const r = await api.productionCleanup(false, resetPhrase.trim());
      toast.success(`Reset complete — ${r.tenants_deleted} tenants, ${r.users_deleted} users, ${r.docs_purged_total} records removed`);
      setResetOpen(false);
      await load();
    } catch (err) { toast.error(err.message || 'Reset failed'); }
    setResetBusy(false);
  };

  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => { const ids = (data?.tenants || []).filter(t => !t.is_protected).map(t => t.tenant_id); setSelected(new Set(ids)); };
  const clearSelect = () => setSelected(new Set());

  const openAction = (tenant, kind) => {
    setActionTenant({ tenant, kind });
    setReason('manual');
    setCustomReason('');
    setConfirmName('');
  };
  const closeAction = () => { setActionTenant(null); setReason('manual'); setCustomReason(''); setConfirmName(''); };

  const performAction = async () => {
    if (!actionTenant) return;
    const { tenant, kind } = actionTenant;
    const finalReason = (reason === 'custom' ? customReason : reason).trim() || 'manual';
    const key = `${tenant.tenant_id}:${kind}`;
    setBusy(b => ({ ...b, [key]: true }));
    try {
      if (kind === 'disable') {
        await api.disableTenant(tenant.tenant_id, finalReason);
        toast.success(`Disabled "${tenant.name}"`);
      } else if (kind === 'enable') {
        await api.enableTenant(tenant.tenant_id);
        toast.success(`Re-enabled "${tenant.name}"`);
      } else if (kind === 'soft') {
        await api.softDeleteTenant(tenant.tenant_id, finalReason);
        toast.success(`Soft-deleted "${tenant.name}" — purges in 30 days`);
      } else if (kind === 'hard') {
        if (confirmName !== tenant.name) {
          toast.error('Confirmation name does not match');
          setBusy(b => { const c = { ...b }; delete c[key]; return c; });
          return;
        }
        await api.hardDeleteTenant(tenant.tenant_id, confirmName, finalReason);
        toast.success(`Permanently deleted "${tenant.name}"`);
      }
      closeAction();
      await load();
    } catch (err) {
      toast.error(err.message || 'Action failed');
    } finally {
      setBusy(b => { const c = { ...b }; delete c[key]; return c; });
    }
  };

  const bulkApply = async (kind) => {
    if (selected.size === 0) return;
    const ids = [...selected];
    if (!window.confirm(`${kind === 'disable' ? 'Disable' : 'Soft-delete'} ${ids.length} tenant(s)?\n\nReason will be set to "manual".${kind === 'soft' ? ' They will be permanently purged in 30 days.' : ''}`)) return;
    const tenants = (data?.tenants || []).filter(t => ids.includes(t.tenant_id));
    let ok = 0, fail = 0;
    for (const t of tenants) {
      try {
        if (kind === 'disable') await api.disableTenant(t.tenant_id, 'manual');
        else if (kind === 'soft') await api.softDeleteTenant(t.tenant_id, 'manual');
        ok++;
      } catch (_) { fail++; }
    }
    toast[fail ? 'warning' : 'success'](`${kind === 'disable' ? 'Disabled' : 'Soft-deleted'}: ${ok} ok${fail ? `, ${fail} failed` : ''}`);
    clearSelect();
    await load();
  };

  const presetExpiredTrials = () => {
    if (!data?.tenants) return;
    const ids = data.tenants
      .filter(t => !t.is_protected && t.trial_state === 'expired' && t.status !== 'suspended' && t.status !== 'pending_deletion')
      .map(t => t.tenant_id);
    setSelected(new Set(ids));
    toast.success(`${ids.length} expired-trial tenant(s) selected`);
  };

  const presetIdle90Days = () => {
    if (!data?.tenants) return;
    const ids = data.tenants
      .filter(t => !t.is_protected && (t.days_since_login == null || t.days_since_login >= 90) && t.status !== 'suspended' && t.status !== 'pending_deletion')
      .map(t => t.tenant_id);
    setSelected(new Set(ids));
    toast.success(`${ids.length} idle ≥90d tenant(s) selected`);
  };

  const tenants = useMemo(() => data?.tenants || [], [data]);
  const counts = data?.counts || {};
  const allSelectable = useMemo(() => tenants.filter(t => !t.is_protected).length, [tenants]);

  if (!isSuperAdmin) {
    return (
      <div className="p-8" data-testid="admin-tenants-forbidden">
        <div className="max-w-md mx-auto rounded-2xl border border-amber-500/30 bg-amber-500/10 p-6 text-center">
          <AlertTriangle className="w-8 h-8 text-amber-500 mx-auto mb-3" />
          <h2 className="text-lg font-semibold mb-1">Super admin only</h2>
          <p className="text-sm text-muted-foreground">Tenant management is restricted to super admins.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-5 max-w-7xl" data-testid="admin-tenants-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold flex items-center gap-2">
            <Building2 className="w-6 h-6 text-blue-400" />
            Tenant Management
            <Badge className="ml-2 text-[10px] bg-muted">{counts.total || 0} total</Badge>
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Disable, soft-delete (30-day grace), or permanently purge tenants. Cleanup expired trials and non-paying customers.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} className="rounded-xl" data-testid="refresh-tenants-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)} className="rounded-xl" data-testid="add-tenant-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Tenant
          </Button>
          <Button variant="destructive" size="sm" onClick={openReset} className="rounded-xl" data-testid="production-reset-btn">
            <Trash2 className="w-4 h-4 mr-1" /> Production Reset
          </Button>
        </div>
      </div>

      {/* Add Tenant dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="add-tenant-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Building2 className="w-4 h-4 text-primary" /> Add Tenant</DialogTitle>
            <DialogDescription>Create a new organization. You can add users and configure it afterwards.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Name <span className="text-destructive">*</span></label>
              <Input value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} placeholder="Acme Insurance" className="mt-1" data-testid="add-tenant-name" />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Domain</label>
              <Input value={createForm.domain} onChange={e => setCreateForm(f => ({ ...f, domain: e.target.value }))} placeholder="acme.com" className="mt-1" data-testid="add-tenant-domain" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Plan</label>
                <Select value={createForm.plan} onValueChange={v => setCreateForm(f => ({ ...f, plan: v }))}>
                  <SelectTrigger className="mt-1" data-testid="add-tenant-plan"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Max users</label>
                <Input type="number" min="1" value={createForm.max_users} onChange={e => setCreateForm(f => ({ ...f, max_users: e.target.value }))} className="mt-1" data-testid="add-tenant-maxusers" />
              </div>
            </div>
            <div className="border-t pt-3 mt-1">
              <div className="flex items-center gap-1.5 mb-2">
                <UserCog className="w-3.5 h-3.5 text-primary" />
                <span className="text-xs font-medium">First admin <span className="text-muted-foreground font-normal">(optional)</span></span>
              </div>
              <p className="text-[11px] text-muted-foreground mb-2">Seed the tenant with its first admin user. They'll receive a set-password email; you'll also get a temporary password to share.</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Admin email</label>
                  <Input type="email" value={createForm.admin_email} onChange={e => setCreateForm(f => ({ ...f, admin_email: e.target.value }))} placeholder="admin@acme.com" className="mt-1" data-testid="add-tenant-admin-email" />
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Admin name</label>
                  <Input value={createForm.admin_name} onChange={e => setCreateForm(f => ({ ...f, admin_name: e.target.value }))} placeholder="Jane Doe" className="mt-1" data-testid="add-tenant-admin-name" />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} disabled={createBusy}>Cancel</Button>
            <Button onClick={createTenant} disabled={createBusy} data-testid="add-tenant-submit">
              {createBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />} Create Tenant
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Seeded-admin result dialog — surfaces the temp password / reset link once */}
      <Dialog open={!!seededAdmin} onOpenChange={o => !o && setSeededAdmin(null)}>
        <DialogContent data-testid="seeded-admin-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><KeyRound className="w-4 h-4 text-emerald-500" /> {seededAdmin?.resent ? 'Invite re-sent' : 'Admin account created'}</DialogTitle>
            <DialogDescription>
              {seededAdmin?.email_sent
                ? `A set-password email was sent to ${seededAdmin?.email}.${seededAdmin?.resent ? ' You can also copy the link below to share directly.' : ' Share the temporary password below as a backup.'}`
                : `Email isn't configured, so share the set-password link${seededAdmin?.temp_password ? ' (or the temporary password)' : ''} with ${seededAdmin?.email}.`}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            {seededAdmin?.temp_password && (
              <div>
                <label className="text-xs font-medium text-muted-foreground">Temporary password</label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 px-3 py-2 rounded-lg bg-muted font-mono text-sm break-all" data-testid="seeded-admin-password">{seededAdmin?.temp_password}</code>
                  <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(seededAdmin?.temp_password || ''); toast.success('Password copied'); }} data-testid="copy-temp-password">
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            )}
            {seededAdmin?.reset_link && (
              <div>
                <label className="text-xs font-medium text-muted-foreground">Set-password link</label>
                <div className="flex items-center gap-2 mt-1">
                  <code className="flex-1 px-3 py-2 rounded-lg bg-muted font-mono text-[11px] break-all">{seededAdmin.reset_link}</code>
                  <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(seededAdmin.reset_link); toast.success('Link copied'); }} data-testid="copy-reset-link">
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            )}
            {seededAdmin?.temp_password && (
              <p className="text-[11px] text-amber-600 dark:text-amber-400">This password is shown only once. The admin should change it after signing in.</p>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setSeededAdmin(null)} data-testid="seeded-admin-done">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit-tenant dialog — plan + max users */}
      <Dialog open={!!editTenant} onOpenChange={o => !o && setEditTenant(null)}>
        <DialogContent data-testid="edit-tenant-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Pencil className="w-4 h-4 text-primary" /> Edit {editTenant?.name}</DialogTitle>
            <DialogDescription>Change this tenant's plan and user limit.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-1">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Plan</label>
              <Select value={editForm.plan} onValueChange={v => setEditForm(f => ({ ...f, plan: v }))}>
                <SelectTrigger className="mt-1" data-testid="edit-tenant-plan"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Max users</label>
              <Input type="number" min="1" value={editForm.max_users} onChange={e => setEditForm(f => ({ ...f, max_users: e.target.value }))} className="mt-1" data-testid="edit-tenant-maxusers" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTenant(null)} disabled={editBusy}>Cancel</Button>
            <Button onClick={saveEdit} disabled={editBusy} data-testid="edit-tenant-submit">
              {editBusy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Pencil className="w-4 h-4 mr-1" />} Save changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {resetOpen && (
        <Dialog open onOpenChange={(o) => !o && setResetOpen(false)}>
          <DialogContent data-testid="production-reset-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <ShieldAlert className="w-5 h-5" /> Production Reset
              </DialogTitle>
              <DialogDescription>
                Permanently deletes ALL tenants, users, content, uploaded datasets and payment history —
                and cancels their Stripe subscriptions. Super admins, the system tenant and platform
                configuration are preserved. This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            {!resetPreview ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                <Loader2 className="w-4 h-4 animate-spin" /> Calculating what would be removed…
              </div>
            ) : (
              <div className="rounded-lg border bg-muted/40 p-3 text-sm space-y-1" data-testid="reset-preview">
                <div className="flex justify-between"><span>Tenants</span><span className="font-semibold tabular-nums">{resetPreview.tenants_deleted}</span></div>
                <div className="flex justify-between"><span>Users (non super-admin)</span><span className="font-semibold tabular-nums">{resetPreview.users_deleted}</span></div>
                <div className="flex justify-between"><span>Records across collections</span><span className="font-semibold tabular-nums">{resetPreview.docs_purged_total.toLocaleString()}</span></div>
                <div className="flex justify-between"><span>Dataset collections dropped</span><span className="font-semibold tabular-nums">{resetPreview.collections_dropped.length}</span></div>
                <div className="flex justify-between"><span>Stripe subscriptions to cancel</span><span className="font-semibold tabular-nums">{resetPreview.stripe_subscriptions_canceled.length}</span></div>
              </div>
            )}
            <div>
              <p className="text-xs text-muted-foreground mb-1.5">Type <span className="font-mono font-semibold">RESET PRODUCTION</span> to confirm:</p>
              <Input value={resetPhrase} onChange={(e) => setResetPhrase(e.target.value)} placeholder="RESET PRODUCTION" data-testid="reset-confirm-input" />
              <p className="text-[11px] text-muted-foreground mt-2">
                Tip: set <span className="font-mono">SEED_DEMO_DATA=false</span> in the production environment so demo content doesn't re-seed on restart.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setResetOpen(false)} disabled={resetBusy} data-testid="reset-cancel-btn">Cancel</Button>
              <Button variant="destructive" onClick={runReset} disabled={resetBusy || resetPhrase.trim() !== 'RESET PRODUCTION'} data-testid="reset-execute-btn">
                {resetBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Erase everything'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Counts */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2" data-testid="tenant-counts">
        <CountCard label="Active"            value={counts.active || 0}            tone="default" icon={CheckCircle2} />
        <CountCard label="Trial"             value={counts.trial || 0}             tone="info"    icon={Clock} />
        <CountCard label="Trial expired"     value={counts.trial_expired || 0}     tone="warn"    icon={AlertCircle} />
        <CountCard label="Suspended"         value={counts.suspended || 0}         tone="warn"    icon={Power} />
        <CountCard label="Pending deletion"  value={counts.pending_deletion || 0}  tone="bad"     icon={Trash2} />
        <CountCard label="Pending approval"  value={counts.pending_approval || 0}  tone="default" icon={ShieldAlert} />
      </div>

      {/* Auto-cleanup banner */}
      {cleanup && (
        <div className="rounded-2xl border bg-card p-4 flex flex-wrap gap-3 items-center justify-between" data-testid="cleanup-banner">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-blue-500/10 border border-blue-500/30 p-2">
              <Bot className="w-4 h-4 text-blue-400" />
            </div>
            <div>
              <div className="text-sm font-medium flex items-center gap-2">
                Tenant auto-cleanup
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border border-emerald-500/30">running</span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Trials that expired more than <strong>{cleanup.config.post_trial_grace_days} days</strong> ago are auto-suspended.
                Tenants in <em>pending deletion</em> are purged after the <strong>{cleanup.config.pending_deletion_grace_days}-day</strong> grace window.
              </p>
              <p className="text-[11px] text-muted-foreground mt-1" data-testid="cleanup-last-run">
                {cleanup.last_run_at ? <>Last run {new Date(cleanup.last_run_at).toLocaleString()} · suspended {cleanup.last_summary?.auto_suspended?.suspended?.length || 0}, purged {cleanup.last_summary?.auto_purged?.purged?.length || 0}</> : 'Has not run yet'}
              </p>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={triggerCleanup} disabled={cleanupRunning} className="rounded-xl" data-testid="run-cleanup-btn">
            {cleanupRunning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
            Run cleanup now
          </Button>
        </div>
      )}

      {/* Filters + presets */}
      <div className="rounded-2xl border bg-card p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            data-testid="tenants-search"
            value={q}
            onChange={e => setQ(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') load(); }}
            placeholder="Search by tenant name or id…"
            className="pl-9 rounded-xl"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] rounded-xl" data-testid="status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="trial">Trial</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
            <SelectItem value="pending_deletion">Pending deletion</SelectItem>
            <SelectItem value="pending_approval">Pending approval</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" onClick={load} className="rounded-xl" data-testid="apply-filters-btn">Apply</Button>
        <span className="text-muted-foreground text-xs ml-2">Quick selects:</span>
        <Button size="sm" variant="outline" onClick={presetExpiredTrials} className="rounded-xl text-xs" data-testid="preset-expired-trials">Expired trials</Button>
        <Button size="sm" variant="outline" onClick={presetIdle90Days} className="rounded-xl text-xs" data-testid="preset-idle-90d">Idle ≥90d</Button>
      </div>

      {/* Bulk action bar (visible only when something selected) */}
      {selected.size > 0 && (
        <div className="rounded-xl border bg-blue-500/10 border-blue-500/30 px-4 py-2.5 flex items-center justify-between" data-testid="bulk-bar">
          <span className="text-sm">
            <strong>{selected.size}</strong> of {allSelectable} selectable · <button onClick={clearSelect} className="underline hover:no-underline">clear</button>
          </span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => bulkApply('disable')} className="rounded-lg text-xs bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300 hover:bg-amber-500/20" data-testid="bulk-disable-btn">
              <Power className="w-3 h-3 mr-1" /> Disable selected
            </Button>
            <Button size="sm" variant="outline" onClick={() => bulkApply('soft')} className="rounded-lg text-xs bg-rose-500/10 border-rose-500/30 text-rose-700 dark:text-rose-300 hover:bg-rose-500/20" data-testid="bulk-soft-btn">
              <Trash2 className="w-3 h-3 mr-1" /> Soft-delete selected
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="rounded-2xl border bg-card overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-muted-foreground" data-testid="tenants-loading">
            <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" /> Loading tenants…
          </div>
        ) : tenants.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground" data-testid="tenants-empty">No tenants match your filters.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-3 py-2 w-8">
                  <input type="checkbox"
                    checked={selected.size > 0 && selected.size === allSelectable}
                    onChange={e => e.target.checked ? selectAll() : clearSelect()}
                    data-testid="select-all-checkbox" />
                </th>
                <th className="px-3 py-2 text-left">Tenant</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Plan</th>
                <th className="px-3 py-2 text-right">Users</th>
                <th className="px-3 py-2 text-right">Dashes</th>
                <th className="px-3 py-2 text-left">Last login</th>
                <th className="px-3 py-2 text-left">Trial / Purge</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {tenants.map(t => (
                <tr key={t.tenant_id} className="border-t hover:bg-muted/30" data-testid={`tenant-row-${t.tenant_id}`}>
                  <td className="px-3 py-2">
                    {!t.is_protected && (
                      <input type="checkbox" checked={selected.has(t.tenant_id)} onChange={() => toggleSelect(t.tenant_id)} data-testid={`select-${t.tenant_id}`} />
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      onClick={() => setUsersTenant(t)}
                      className="text-left group/tname"
                      data-testid={`open-users-${t.tenant_id}`}
                      title="Click to view users in this tenant"
                    >
                      <div className="font-medium flex items-center gap-1.5 group-hover/tname:text-primary transition-colors">
                        {t.name}
                        {t.is_protected && <ShieldAlert className="w-3 h-3 text-amber-500" />}
                      </div>
                    </button>
                  </td>
                  <td className="px-3 py-2"><StatusBadge status={t.status} /></td>
                  <td className="px-3 py-2 text-xs">{t.plan}</td>
                  <td className="px-3 py-2 text-right text-xs">
                    <button
                      onClick={() => setUsersTenant(t)}
                      className="inline-flex items-center gap-1 hover:text-primary transition-colors"
                      data-testid={`users-count-${t.tenant_id}`}
                      title="View users"
                    >
                      <Users className="w-3 h-3" /> {t.user_count}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-right text-xs">{t.dashboard_count}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {t.days_since_login == null ? '—' : t.days_since_login === 0 ? 'today' : `${t.days_since_login}d ago`}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {t.scheduled_delete_at && (
                      <span className="text-rose-700 dark:text-rose-300">purge in {t.days_to_purge}d</span>
                    )}
                    {!t.scheduled_delete_at && t.trial_state === 'expired' && <span className="text-amber-700 dark:text-amber-300">trial expired</span>}
                    {!t.scheduled_delete_at && t.trial_state === 'expiring_soon' && <span className="text-amber-700 dark:text-amber-300">expires {t.days_to_trial_end}d</span>}
                    {!t.scheduled_delete_at && t.trial_state === 'active' && <span className="text-muted-foreground">{t.days_to_trial_end}d left</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {t.is_protected ? (
                      <span className="text-[10px] text-muted-foreground italic">protected</span>
                    ) : (
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="outline" onClick={() => openEdit(t)} className="rounded-md h-7 px-2 text-[11px]" data-testid={`btn-edit-${t.tenant_id}`}>
                          <Pencil className="w-3 h-3 mr-1" /> Edit
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => resendInvite(t)} disabled={resendingId === t.tenant_id} className="rounded-md h-7 px-2 text-[11px]" data-testid={`btn-resend-invite-${t.tenant_id}`} title="Resend the set-password email to this tenant's first admin">
                          {resendingId === t.tenant_id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <KeyRound className="w-3 h-3 mr-1" />} Resend invite
                        </Button>
                        {(t.status === 'suspended' || t.status === 'pending_deletion') && (
                          <Button size="sm" variant="outline" onClick={() => openAction(t, 'enable')} className="rounded-md h-7 px-2 text-[11px] bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-500/20" data-testid={`btn-enable-${t.tenant_id}`}>
                            <RotateCcw className="w-3 h-3 mr-1" /> Restore
                          </Button>
                        )}
                        {t.status !== 'suspended' && t.status !== 'pending_deletion' && (
                          <Button size="sm" variant="outline" onClick={() => openAction(t, 'disable')} className="rounded-md h-7 px-2 text-[11px] bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300 hover:bg-amber-500/20" data-testid={`btn-disable-${t.tenant_id}`}>
                            <Power className="w-3 h-3 mr-1" /> Disable
                          </Button>
                        )}
                        {t.status !== 'pending_deletion' && (
                          <Button size="sm" variant="outline" onClick={() => openAction(t, 'soft')} className="rounded-md h-7 px-2 text-[11px] bg-rose-500/10 border-rose-500/30 text-rose-700 dark:text-rose-300 hover:bg-rose-500/20" data-testid={`btn-soft-${t.tenant_id}`}>
                            <Trash2 className="w-3 h-3 mr-1" /> Soft-delete
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => openAction(t, 'hard')} className="rounded-md h-7 px-2 text-[11px] bg-rose-500/15 border-rose-500/40 text-rose-700 dark:text-rose-300 hover:bg-rose-500/25" data-testid={`btn-hard-${t.tenant_id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Action dialog */}
      <Dialog open={!!actionTenant} onOpenChange={open => !open && closeAction()}>
        <DialogContent data-testid="action-dialog">
          {actionTenant && (() => {
            const { tenant, kind } = actionTenant;
            const titleMap = {
              disable: 'Disable tenant',
              enable:  'Re-enable tenant',
              soft:    'Schedule tenant for deletion',
              hard:    'Permanently delete tenant',
            };
            const descMap = {
              disable: 'Blocks login for all users in this tenant. Data is preserved. Reversible at any time.',
              enable:  'Restores login access for all users in this tenant. Status returns to its prior state.',
              soft:    `The tenant will be hidden from users immediately, and ALL data will be permanently purged in 30 days unless restored. Reversible until the grace period expires.`,
              hard:    'This permanently purges users, dashboards, schedules, alerts, reports and semantic models for this tenant. Audit logs are preserved for compliance. THIS CANNOT BE UNDONE.',
            };
            return (
              <>
                <DialogHeader>
                  <DialogTitle>{titleMap[kind]} — {tenant.name}</DialogTitle>
                  <DialogDescription>{descMap[kind]}</DialogDescription>
                </DialogHeader>
                {kind !== 'enable' && (
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-medium block mb-1">Reason</label>
                      <Select value={reason} onValueChange={setReason}>
                        <SelectTrigger className="rounded-lg" data-testid="reason-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {REASON_PRESETS.map(r => <SelectItem key={r} value={r}>{r.replace(/_/g, ' ')}</SelectItem>)}
                          <SelectItem value="custom">Custom…</SelectItem>
                        </SelectContent>
                      </Select>
                      {reason === 'custom' && (
                        <Input value={customReason} onChange={e => setCustomReason(e.target.value)} placeholder="Enter reason…" className="mt-2 rounded-lg" data-testid="custom-reason-input" />
                      )}
                    </div>
                    {kind === 'hard' && (
                      <div className="rounded-lg border border-rose-500/30 bg-rose-500/10 p-3">
                        <p className="text-xs font-medium text-rose-700 dark:text-rose-300 mb-2">
                          To confirm, type the tenant name exactly: <code className="font-mono text-foreground">{tenant.name}</code>
                        </p>
                        <Input
                          value={confirmName}
                          onChange={e => setConfirmName(e.target.value)}
                          placeholder={tenant.name}
                          className="rounded-lg"
                          data-testid="confirm-name-input"
                        />
                      </div>
                    )}
                  </div>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={closeAction} data-testid="action-cancel">Cancel</Button>
                  <Button
                    onClick={performAction}
                    disabled={kind === 'hard' && confirmName !== tenant.name}
                    className={kind === 'hard' ? 'bg-rose-500 hover:bg-rose-600 text-white' : (kind === 'enable' ? 'bg-emerald-500 hover:bg-emerald-600 text-white' : 'bg-blue-500 hover:bg-blue-600 text-white')}
                    data-testid="action-confirm"
                  >
                    {kind === 'disable' ? 'Disable' : kind === 'enable' ? 'Re-enable' : kind === 'soft' ? 'Schedule deletion' : 'Permanently delete'}
                  </Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Users-of-tenant drawer */}
      <TenantUsersDialog tenant={usersTenant} onClose={() => setUsersTenant(null)} />
    </div>
  );
}


// ───────────── TenantUsersDialog ─────────────────────────────────────────
// Lists every user in the selected tenant and exposes a row of admin
// actions per user: send password-reset email, set password directly,
// change role, enable/disable, delete. All actions hit endpoints gated to
// super_admin on the backend.
function TenantUsersDialog({ tenant, onClose }) {
  const { user: currentUser, setUserData } = useAuth();
  const { confirmFn, ConfirmDialogElement } = useConfirmDialog();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [pendingId, setPendingId] = useState(null);     // user_id of in-flight action
  const [setPwdFor, setSetPwdFor] = useState(null);     // user_id targeted by the password dialog
  const [newPwd, setNewPwd] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', email: '', password: '', role: 'viewer' });
  const [adding, setAdding] = useState(false);

  const handleAddUser = async () => {
    if (!addForm.email || !addForm.password) { toast.error('Email and password are required'); return; }
    setAdding(true);
    try {
      await api.adminCreateTenantUser(tenant.tenant_id, addForm);
      toast.success(`${addForm.email} added to ${tenant.name}`);
      setAddForm({ name: '', email: '', password: '', role: 'viewer' });
      setAddOpen(false);
      load();
    } catch (e) { toast.error(e.message || 'Could not add user'); }
    finally { setAdding(false); }
  };

  const load = useCallback(async () => {
    if (!tenant?.tenant_id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.adminListTenantUsers(tenant.tenant_id);
      setUsers(res?.users || []);
    } catch (e) {
      setError(e.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [tenant?.tenant_id]);

  useEffect(() => { if (tenant) load(); else { setUsers([]); setError(null); } }, [tenant, load]);

  const handleSendReset = async (u) => {
    setPendingId(u.user_id);
    try {
      const res = await api.adminSendPasswordReset(u.user_id);
      toast.success(res?.email_sent ? `Reset email sent to ${u.email}` : `Reset link generated (email not configured)`);
    } catch (e) { toast.error(e.message); }
    finally { setPendingId(null); }
  };

  const handleSetPassword = async () => {
    if (!setPwdFor || !newPwd) return;
    setPendingId(setPwdFor);
    try {
      await api.adminSetUserPassword(setPwdFor, newPwd);
      toast.success('Password updated');
      setSetPwdFor(null); setNewPwd('');
    } catch (e) { toast.error(e.message); }
    finally { setPendingId(null); }
  };

  const handleStatus = async (u, newStatus) => {
    setPendingId(u.user_id);
    try {
      await api.updateUserStatus(u.user_id, { status: newStatus });
      setUsers(prev => prev.map(x => x.user_id === u.user_id ? { ...x, status: newStatus } : x));
      toast.success(newStatus === 'disabled' ? `${u.email} disabled` : `${u.email} re-enabled`);
    } catch (e) { toast.error(e.message); }
    finally { setPendingId(null); }
  };

  const handleRole = async (u, role) => {
    setPendingId(u.user_id);
    try {
      await api.adminUpdateUserRole(u.user_id, role);
      setUsers(prev => prev.map(x => x.user_id === u.user_id ? { ...x, role } : x));
      toast.success(`Role changed to ${role}`);
    } catch (e) { toast.error(e.message); }
    finally { setPendingId(null); }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setPendingId(confirmDelete.user_id);
    try {
      await api.deleteUser(confirmDelete.user_id);
      setUsers(prev => prev.filter(x => x.user_id !== confirmDelete.user_id));
      toast.success(`${confirmDelete.email} deleted`);
      setConfirmDelete(null);
    } catch (e) { toast.error(e.message); }
    finally { setPendingId(null); }
  };

  const handleImpersonate = async (u) => {
    // Mirror the RecentImpersonationsPanel flow exactly: confirm → swap
    // session cookie via /auth/impersonate → drop a localStorage marker so
    // the impersonation banner kicks in → reload into /dashboard with the
    // target user's context. End-impersonation lives in the banner UI.
    const ok = await confirmFn({
      title: 'Impersonate this user?',
      message: `You will sign in as ${u.name || u.email} in tenant ${tenant.name}. Every action you take is audit-logged under your super-admin identity. Click "End impersonation" in the red banner to return.`,
      confirmLabel: 'Impersonate',
      destructive: true,
    });
    if (!ok) return;
    setPendingId(u.user_id);
    try {
      const res = await api.impersonateUser(u.user_id);
      localStorage.setItem('impersonating', JSON.stringify({ admin_user: currentUser, started_at: new Date().toISOString(), return_to: window.location.pathname + window.location.search }));
      setUserData(res.user);
      toast.success(`Impersonating ${u.name || u.email}`);
      window.location.href = '/dashboard';
    } catch (e) {
      toast.error(e.message || 'Failed to start impersonation');
    } finally {
      setPendingId(null);
    }
  };

  if (!tenant) return null;

  return (
    <Dialog open={!!tenant} onOpenChange={open => !open && onClose()}>
      <DialogContent className="sm:max-w-3xl" data-testid="tenant-users-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-4 h-4 text-primary" />
            Users in <span className="text-primary">{tenant.name}</span>
          </DialogTitle>
          <DialogDescription className="text-[11px] opacity-70">Manage users and roles for this tenant.</DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-10 flex items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading users…
          </div>
        )}
        {error && (
          <div className="rounded-lg border border-rose-500/30 bg-rose-500/5 p-3 text-sm text-rose-700 dark:text-rose-300">{error}</div>
        )}

        <div className="rounded-lg border border-border/50">
          <button type="button" onClick={() => setAddOpen(o => !o)}
            className="w-full flex items-center justify-between px-3 py-2 text-sm text-foreground hover:bg-muted/40 rounded-lg" data-testid="tenant-add-user-toggle">
            <span className="flex items-center gap-2"><UserPlus className="w-4 h-4 text-primary" /> Add a user to this tenant</span>
            <ChevronDown className={`w-4 h-4 transition-transform ${addOpen ? 'rotate-180' : ''}`} />
          </button>
          {addOpen && (
            <div className="p-3 pt-1 grid grid-cols-1 sm:grid-cols-2 gap-2 border-t border-border/40">
              <Input placeholder="Name" value={addForm.name} onChange={e => setAddForm(f => ({ ...f, name: e.target.value }))} data-testid="tenant-add-user-name" />
              <Input type="email" placeholder="Email" value={addForm.email} onChange={e => setAddForm(f => ({ ...f, email: e.target.value }))} data-testid="tenant-add-user-email" />
              <Input type="password" placeholder="Temporary password" value={addForm.password} onChange={e => setAddForm(f => ({ ...f, password: e.target.value }))} data-testid="tenant-add-user-password" />
              <select value={addForm.role} onChange={e => setAddForm(f => ({ ...f, role: e.target.value }))} className="h-9 rounded-md border border-border/50 bg-background px-2 text-sm" data-testid="tenant-add-user-role">
                <option value="viewer">Viewer</option>
                <option value="analyst">Analyst</option>
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
              </select>
              <div className="sm:col-span-2 flex justify-end">
                <Button size="sm" onClick={handleAddUser} disabled={adding} data-testid="tenant-add-user-submit">
                  {adding ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <UserPlus className="w-3.5 h-3.5 mr-1" />} Add user
                </Button>
              </div>
            </div>
          )}
        </div>
        {!loading && !error && users.length === 0 && (
          <div className="py-10 text-center text-muted-foreground text-sm">No users in this tenant.</div>
        )}

        {!loading && users.length > 0 && (
          <div className="rounded-xl border overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-muted/40 text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">User</th>
                  <th className="px-3 py-2 text-left">Role</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Last login</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const isPending = pendingId === u.user_id;
                  const isSuper = u.role === 'super_admin';
                  const isDisabled = u.status === 'disabled';
                  return (
                    <tr key={u.user_id} className="border-t hover:bg-muted/30" data-testid={`user-row-${u.user_id}`}>
                      <td className="px-3 py-2">
                        <div className="font-medium">{u.name || u.email}</div>
                        <div className="text-[11px] text-muted-foreground">{u.email}</div>
                        {/* Per-user activity glance: dashboards owned / schedules / explore questions (30d) */}
                        {u.stats && (u.stats.dashboards_owned > 0 || u.stats.schedules_owned > 0 || u.stats.explore_30d > 0) ? (
                          <div className="flex items-center gap-1.5 mt-1 text-[10px] text-muted-foreground" data-testid={`user-stats-${u.user_id}`}>
                            {u.stats.dashboards_owned > 0 && (
                              <span className="inline-flex items-center gap-0.5" title="Dashboards + reports owned">
                                <BarChart3 className="w-2.5 h-2.5" /> {u.stats.dashboards_owned}
                              </span>
                            )}
                            {u.stats.schedules_owned > 0 && (
                              <span className="inline-flex items-center gap-0.5" title="Scheduled deliveries owned">
                                <Calendar className="w-2.5 h-2.5" /> {u.stats.schedules_owned}
                              </span>
                            )}
                            {u.stats.explore_30d > 0 && (
                              <span className="inline-flex items-center gap-0.5 text-primary/80" title="AI Copilot questions asked in the last 30 days">
                                <Bot className="w-2.5 h-2.5" /> {u.stats.explore_30d}
                                <span className="opacity-60">·30d</span>
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="text-[10px] text-muted-foreground/70 italic mt-1" data-testid={`user-stats-${u.user_id}`}>
                            no activity yet
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <Select value={u.role || 'viewer'} onValueChange={v => handleRole(u, v)} disabled={isPending || isSuper}>
                          <SelectTrigger className="h-7 w-32 text-xs" data-testid={`role-${u.user_id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="viewer">Viewer</SelectItem>
                            <SelectItem value="analyst">Analyst</SelectItem>
                            <SelectItem value="manager">Manager</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="owner">Owner</SelectItem>
                            {isSuper && <SelectItem value="super_admin">Super admin</SelectItem>}
                          </SelectContent>
                        </Select>
                        {isSuper && <div className="text-[9px] text-amber-600 dark:text-amber-400 mt-0.5 italic">protected</div>}
                      </td>
                      <td className="px-3 py-2">
                        {isDisabled ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-medium bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30">disabled</span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-medium bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30">active</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-muted-foreground">
                        {u.last_login_at ? fmtTime(u.last_login_at) : '—'}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-1 flex-wrap">
                          <Button size="sm" variant="outline" className="h-7 px-2 text-[10px] bg-violet-500/10 border-violet-500/30 text-violet-700 dark:text-violet-300 hover:bg-violet-500/20"
                            onClick={() => handleImpersonate(u)}
                            disabled={isPending || isSuper || u.user_id === currentUser?.user_id}
                            data-testid={`impersonate-${u.user_id}`}
                            title={u.user_id === currentUser?.user_id ? "That's you" : isSuper ? "Cannot impersonate a super admin" : "Sign in as this user (audit-logged)"}>
                            <UserCog className="w-3 h-3 mr-1" /> Impersonate
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 px-2 text-[10px]"
                            onClick={() => handleSendReset(u)}
                            disabled={isPending}
                            data-testid={`send-reset-${u.user_id}`}
                            title="Email this user a password-reset link">
                            {isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3 mr-1" />}
                            Send reset
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 px-2 text-[10px]"
                            onClick={() => { setSetPwdFor(u.user_id); setNewPwd(''); }}
                            disabled={isPending || (isSuper && u.user_id !== tenant.tenant_id)}
                            data-testid={`set-pwd-${u.user_id}`}
                            title="Set this user's password directly">
                            Set password
                          </Button>
                          {isDisabled ? (
                            <Button size="sm" variant="outline" className="h-7 px-2 text-[10px] bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-300"
                              onClick={() => handleStatus(u, 'active')} disabled={isPending} data-testid={`enable-${u.user_id}`}>
                              <CheckCircle2 className="w-3 h-3 mr-1" /> Enable
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" className="h-7 px-2 text-[10px] bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300"
                              onClick={() => handleStatus(u, 'disabled')} disabled={isPending || isSuper} data-testid={`disable-${u.user_id}`}>
                              <Power className="w-3 h-3 mr-1" /> Disable
                            </Button>
                          )}
                          <Button size="sm" variant="outline" className="h-7 px-2 text-[10px] bg-rose-500/10 border-rose-500/30 text-rose-700 dark:text-rose-300"
                            onClick={() => setConfirmDelete(u)} disabled={isPending || isSuper} data-testid={`delete-${u.user_id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <DialogFooter className="text-[10px] text-muted-foreground italic">
          All actions are super-admin-only and audit-logged. Email-based reset
          requires SendGrid; use &ldquo;Set password&rdquo; when it isn&apos;t configured.
        </DialogFooter>
      </DialogContent>

      {/* Set password sub-dialog */}
      <Dialog open={!!setPwdFor} onOpenChange={open => !open && setSetPwdFor(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="set-password-dialog">
          <DialogHeader>
            <DialogTitle>Set new password</DialogTitle>
            <DialogDescription>
              Directly set a new password for{' '}
              <span className="font-medium">{users.find(u => u.user_id === setPwdFor)?.email}</span>.
              Must meet strength requirements; will be audit-logged.
            </DialogDescription>
          </DialogHeader>
          <Input
            type="password"
            value={newPwd}
            onChange={e => setNewPwd(e.target.value)}
            placeholder="New password (12+ chars, mixed case + number)"
            autoFocus
            data-testid="new-password-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setSetPwdFor(null)}>Cancel</Button>
            <Button onClick={handleSetPassword} disabled={!newPwd || pendingId === setPwdFor} data-testid="confirm-set-password">
              {pendingId === setPwdFor ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
              Update password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation sub-dialog */}
      <Dialog open={!!confirmDelete} onOpenChange={open => !open && setConfirmDelete(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="delete-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-700 dark:text-rose-300">Delete user?</DialogTitle>
            <DialogDescription>
              Permanently removes <span className="font-medium">{confirmDelete?.email}</span> from{' '}
              <span className="font-medium">{tenant.name}</span>. This cannot be undone.
              Their authored dashboards/reports stay with the tenant.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={pendingId === confirmDelete?.user_id} data-testid="confirm-delete-user">
              {pendingId === confirmDelete?.user_id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Impersonation confirm-dialog render target */}
      <ConfirmDialogElement />
    </Dialog>
  );
}
